import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError, toApiError } from '@/shared/api/api-error';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';

/** Server routes (`admin:backup`). */
export const DATA_BACKUP_EXPORT_URL = '/api/admin/backup/export';
export const DATA_BACKUP_SNAPSHOT_URL = '/api/admin/backup/snapshot';
export const DATA_BACKUP_STATUS_URL = '/api/admin/backup/status';
export const DATA_BACKUP_RESTORE_URL = '/api/admin/backup/restore';

/** What the user must type to apply a restore. */
export const RESTORE_CONFIRMATION_WORD = 'RESTORE';

/** Fallback when the response carries no `content-disposition` file name. */
const FALLBACK_FILE_NAME = 'complan-backup.jsonl.gz';

export interface DataBackupStatus {
  readonly restoreEnabled: boolean;
  readonly maxUploadBytes: number;
  readonly snapshot: { readonly createdAt: string; readonly migrationLevel: string | null; readonly rowCount: number } | null;
}

export interface RestoreResult {
  readonly mode: 'preview' | 'apply';
  readonly sha256: string;
  readonly accepted: boolean;
  readonly committed: boolean;
  readonly reason: string | null;
  readonly report: {
    readonly tables: readonly { table: string; inBackup: number; before: number; after: number }[];
    readonly pruned: readonly string[];
    readonly remapped: readonly string[];
  } | null;
}

function fileNameOf(response: Response): string {
  const disposition = response.headers.get('content-disposition') ?? '';
  return /filename="([^"]+)"/u.exec(disposition)?.[1] ?? FALLBACK_FILE_NAME;
}

async function toRequestError(response: Response): Promise<ApiRequestError> {
  const correlationId = response.headers.get('x-correlation-id') ?? '';
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // A non-JSON error body still maps to a typed error.
  }
  return new ApiRequestError(response.status, toApiError(response.status, body, correlationId));
}

/**
 * Fetches a gzipped file and hands it to the browser as a download.
 *
 * Not `apiFetch`: that wrapper reads every body as JSON text, and this one is
 * binary. Errors still map to the shared {@link ApiRequestError} shape.
 */
async function downloadFile(url: string): Promise<void> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw await toRequestError(response);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileNameOf(response);
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}

export const downloadDataBackup = (): Promise<void> => downloadFile(DATA_BACKUP_EXPORT_URL);

/** Uploads `file` for a dry run (`preview`) or the real restore (`apply`, with the preview's hash). */
export async function uploadBackup(file: Blob, mode: 'preview' | 'apply', sha256?: string): Promise<RestoreResult> {
  const query = new URLSearchParams({ mode, ...(sha256 === undefined ? {} : { sha256 }) });
  const response = await fetch(`${DATA_BACKUP_RESTORE_URL}?${query.toString()}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/gzip', Accept: 'application/json' },
    body: file,
  });
  if (!response.ok) throw await toRequestError(response);
  return (await response.json()) as RestoreResult;
}

function useDataBackupStatus() {
  return useQuery({
    queryKey: queryKeys.dataBackupStatus(),
    queryFn: () => apiFetch<DataBackupStatus>(DATA_BACKUP_STATUS_URL),
  });
}

function ErrorLine({ reference }: { readonly reference: string | null }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (reference === null) return null;
  return (
    <p role="alert" className="text-[12px] text-destructive">
      {t('admin.operations.dataBackup.failed')}
      {reference !== '' ? ` ${t('error.reference', { correlationId: reference })}` : ''}
    </p>
  );
}

const referenceOf = (error: unknown): string =>
  error instanceof ApiRequestError ? error.body.correlationId : '';

/**
 * The Data backup card on the Operations tab. Rendered only for a principal
 * holding `admin:backup`; the server checks the same capability on every route.
 *
 * Download is always offered. Upload (restore) appears only where the server
 * runs with COMPLAN_ALLOW_UI_RESTORE, and always runs as preview first: the
 * restore itself needs the preview's file hash and the typed confirmation word.
 */
export function DataBackupCard(): React.JSX.Element {
  const { t } = useTranslation();
  const status = useDataBackupStatus();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      await action();
    } catch (error) {
      setFailure(referenceOf(error));
    } finally {
      setBusy(false);
    }
  };

  const snapshot = status.data?.snapshot ?? null;

  return (
    <Card className="gap-3 p-[14px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-[60ch]">
          <p className="text-[13px] font-semibold text-txt">{t('admin.operations.dataBackup.heading')}</p>
          <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.operations.dataBackup.hint')}</p>
        </div>
        <Button type="button" onClick={() => void run(downloadDataBackup)} disabled={busy} aria-busy={busy}>
          {busy ? t('admin.operations.dataBackup.downloading') : t('admin.operations.dataBackup.download')}
        </Button>
      </div>
      <ErrorLine reference={failure} />

      {snapshot !== null ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-3">
          <p className="text-[12px] text-txt">
            {t('admin.operations.dataBackup.snapshot.available', {
              date: new Date(snapshot.createdAt).toLocaleString(),
              count: snapshot.rowCount,
            })}
          </p>
          <Button type="button" variant="outline" disabled={busy} onClick={() => void run(() => downloadFile(DATA_BACKUP_SNAPSHOT_URL))}>
            {t('admin.operations.dataBackup.snapshot.download')}
          </Button>
        </div>
      ) : null}

      {status.data?.restoreEnabled === true ? <RestorePanel maxUploadBytes={status.data.maxUploadBytes} /> : null}
    </Card>
  );
}

function RestorePanel({ maxUploadBytes }: { readonly maxUploadBytes: number }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RestoreResult | null>(null);
  const [applied, setApplied] = useState<RestoreResult | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const tooLarge = file !== null && file.size > maxUploadBytes;

  const reset = (next: File | null): void => {
    setFile(next);
    setPreview(null);
    setApplied(null);
    setConfirmation('');
    setFailure(null);
  };

  const onPreview = async (): Promise<void> => {
    if (file === null) return;
    setBusy(true);
    setFailure(null);
    try {
      setPreview(await uploadBackup(file, 'preview'));
    } catch (error) {
      setFailure(referenceOf(error));
    } finally {
      setBusy(false);
    }
  };

  const onApply = async (): Promise<void> => {
    if (file === null || preview === null) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await uploadBackup(file, 'apply', preview.sha256);
      setApplied(result);
      if (result.committed) {
        setPreview(null);
        setConfirmation('');
        if (inputRef.current !== null) inputRef.current.value = '';
        setFile(null);
        // Every screen's data just changed.
        await queryClient.invalidateQueries();
      }
    } catch (error) {
      setFailure(referenceOf(error));
    } finally {
      setBusy(false);
    }
  };

  const result = applied ?? preview;

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
      <div>
        <p className="text-[12.5px] font-semibold text-txt">{t('admin.operations.dataBackup.restore.heading')}</p>
        <p className="text-muted-foreground mt-1 max-w-[70ch] text-[11px]">{t('admin.operations.dataBackup.restore.hint')}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[12px] text-txt">
          <span>{t('admin.operations.dataBackup.restore.fileLabel')}</span>
          <input
            ref={inputRef}
            type="file"
            accept=".gz,application/gzip"
            disabled={busy}
            onChange={(event) => reset(event.target.files?.[0] ?? null)}
            className="text-[12px]"
          />
        </label>
        <Button type="button" variant="outline" disabled={busy || file === null || tooLarge} onClick={() => void onPreview()}>
          {busy && preview === null ? t('admin.operations.dataBackup.restore.checking') : t('admin.operations.dataBackup.restore.check')}
        </Button>
      </div>
      {tooLarge ? (
        <p role="alert" className="text-[12px] text-destructive">
          {t('admin.operations.dataBackup.restore.tooLarge', { mb: Math.floor(maxUploadBytes / (1024 * 1024)) })}
        </p>
      ) : null}
      <ErrorLine reference={failure} />

      {result !== null && !result.accepted ? (
        <p role="alert" className="text-[12px] text-destructive">
          {t('admin.operations.dataBackup.restore.refused', { reason: result.reason ?? '' })}
        </p>
      ) : null}

      {applied?.committed === true ? (
        <p role="status" className="text-[12px] font-semibold text-txt">
          {t('admin.operations.dataBackup.restore.done')}
        </p>
      ) : null}

      {result?.accepted === true && result.report !== null ? <RestoreReport result={result} /> : null}

      {preview?.accepted === true && applied?.committed !== true ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-[12px] text-txt">
            <span>{t('admin.operations.dataBackup.restore.confirmLabel', { word: RESTORE_CONFIRMATION_WORD })}</span>
            <input
              type="text"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              className="border-line h-8 w-[180px] rounded-[var(--radius-control)] border bg-panel px-2 font-mono text-[12.5px] text-txt outline-none focus-visible:border-[var(--accent)]"
            />
          </label>
          <Button
            type="button"
            variant="destructive"
            disabled={busy || confirmation !== RESTORE_CONFIRMATION_WORD}
            aria-busy={busy}
            onClick={() => void onApply()}
          >
            {busy ? t('admin.operations.dataBackup.restore.applying') : t('admin.operations.dataBackup.restore.apply')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RestoreReport({ result }: { readonly result: RestoreResult }): React.JSX.Element {
  const { t } = useTranslation();
  const report = result.report;
  const rows = (report?.tables ?? []).filter((row) => row.before !== 0 || row.after !== 0 || row.inBackup !== 0);
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[12px] text-txt">
        {result.committed ? t('admin.operations.dataBackup.restore.reportApplied') : t('admin.operations.dataBackup.restore.reportPreview')}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">{t('admin.operations.dataBackup.restore.colTable')}</TableHead>
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.dataBackup.restore.colBefore')}</TableHead>
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.dataBackup.restore.colAfter')}</TableHead>
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.operations.dataBackup.restore.colInBackup')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.table}>
              <TableCell className="font-mono text-[12px]">{row.table}</TableCell>
              <TableCell className="text-right text-[12.3px] tabular-nums">{row.before}</TableCell>
              <TableCell className="text-right text-[12.3px] tabular-nums">{row.after}</TableCell>
              <TableCell className="text-right text-[12.3px] tabular-nums">{row.inBackup}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {[...(report?.pruned ?? []), ...(report?.remapped ?? [])].length > 0 ? (
        <ul className="text-muted-foreground list-disc pl-5 text-[11.5px]">
          {(report?.pruned ?? []).map((line) => (
            <li key={`p-${line}`}>{t('admin.operations.dataBackup.restore.pruned', { detail: line })}</li>
          ))}
          {(report?.remapped ?? []).map((line) => (
            <li key={`r-${line}`}>{t('admin.operations.dataBackup.restore.remapped', { detail: line })}</li>
          ))}
        </ul>
      ) : null}
      <p className="text-muted-foreground text-[11px]">{t('admin.operations.dataBackup.restore.usersKept')}</p>
    </div>
  );
}
